/* eslint-disable i18next/no-literal-string */

import React, { ComponentProps, useState, FocusEvent, useEffect } from "react";
import { cn } from "../../lib/utils";
import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import { Language, useLanguage } from "@/hooks/use-language";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { getFeatureFlags } from "@/lib/runtime-config";
import { i18n } from "@/lib/i18n";
import { useDisableModalHideOnOutsideClick } from "../modal";

type Mode = "normal" | "shift" | "specChars";

interface InputProps extends ComponentProps<"input"> {}
export type InputType =
  | "text"
  | "email"
  | "number"
  | "hidden"
  | "color"
  | "search"
  | "tel"
  | "username"
  | "login"
  | "password"
  | "taxnumber";

export function Input({
  className,
  disableHorizontalGrowth = false,
  uppercase,
  capitalize = false,
  size = "md",
  onEnterSubmit,
  ...props
}: Omit<InputProps, "type" | "onChange" | "size"> & {
  type: InputType;
  onChange?: (value: string) => void;
  capitalize?: boolean;
  uppercase?: boolean;
  disableHorizontalGrowth?: boolean;
  size?: "md" | "lg";
  onEnterSubmit?: () => void;
  tabIndex: number;
}) {
  const value = props.value ? String(props.value) : props.value;
  const [mode, setMode] = useState<Mode>("normal");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [focused, setFocused] = React.useState(false);
  const language = useLanguage();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  useDisableModalHideOnOutsideClick({
    shouldHideModalOnOutsideClick: focused ? false : undefined,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      if (
        document.activeElement &&
        !document.activeElement.isEqualNode(inputRef.current)
      ) {
        setFocused(false);
      }
    });

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <div className="w-full relative min-h-[65px] flex items-center">
        <input className="text-[30px] w-full border-2 opacity-0" />
        <input
          className={cn(
            "w-full px-3 py-2 rounded-md absolute  text-[30px] border-bub-dark-blue border-2 bg-white",
            props.disabled && "bg-gray-200 text-gray-500",
            className,
            !disableHorizontalGrowth &&
              focused &&
              props.value &&
              String(props.value).length > 13 &&
              // Forms set the resting width with `[&_input]:w-*`, which beats
              // a plain width utility. The important width is what lets a long
              // value span the screen.
              "fixed left-[2vw] w-[96vw]! z-20",
          )}
          {...props}
          type={
            props.type === "taxnumber"
              ? "text"
              : props.type === "password" && !showPassword
                ? "password"
                : "text"
          }
          value={value}
          ref={inputRef}
          onChange={(e) =>
            props.onChange?.(
              uppercase ? e.target.value.toUpperCase() : e.target.value,
            )
          }
          onFocus={(e) => {
            setFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            const anotherElementGotFocus = e.relatedTarget != null;
            if (!getFeatureFlags().stickyKeyboard || anotherElementGotFocus) {
              setFocused(false);
            }
            if (e) {
              props.onBlur?.(e);
            }
          }}
        />
        {props.type === "password" && (
          <div
            className={cn(
              "p-4 font-helvetica font-bold absolute right-4 ",
              size === "md" ? "text-[24px]" : "text-[32px]",
            )}
            onClick={() => setShowPassword(!showPassword)}
          >
            {!showPassword ? t(`Mutat`) : t(`Elrejt`)}
          </div>
        )}
      </div>
      {focused &&
        createPortal(
          <div
            className="fixed bottom-4 left-4 right-4 z-100 text-[24px] [&_.hg-button]:h-[5.8vh]!"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Keyboard
              // onChange={(value) => props.onChange?.(value)}
              onKeyPress={(button) => {
                const applyToInput = (char?: string) => {
                  const input = inputRef.current;
                  if (input) {
                    input.setRangeText(
                      char ?? button,
                      input.selectionStart!,
                      input.selectionEnd!,
                      "end",
                    );
                    const event = new Event("input", { bubbles: true });
                    input.dispatchEvent(event);
                  }
                };

                if (button === "{enter}") {
                  // 100ms delay is needed because otherwise something weird
                  // causes modals to close upon pressing this... has to do with
                  // how react batches state updates i think because 50ms delay isn't
                  // enough to get rid of this behavior
                  setTimeout(() => {
                    setFocused(false);
                    if (getFeatureFlags().stickyKeyboard) {
                      const nextInput =
                        document.querySelector<HTMLInputElement>(
                          `input[tabindex="${(props.tabIndex ?? 0) + 1}"]`,
                        );
                      console.log("nextInput", nextInput);
                      if (nextInput) {
                        nextInput.focus();
                      } else {
                        inputRef.current?.blur();
                        onEnterSubmit?.();
                      }
                    } else {
                      // @ts-ignore
                      document.activeElement?.blur?.();
                    }
                  }, 100);
                  return;
                }

                if (button === "{shift}") {
                  setMode(mode === "normal" ? "shift" : "normal");
                  return;
                }
                if (button === "{specChars}" || button === "{abc}") {
                  setMode(mode === "normal" ? "specChars" : "normal");
                  return;
                }
                if (button === "{space}") {
                  applyToInput(" ");
                  return;
                }
                if (button === "{backspace}") {
                  if (props.value) {
                    const input = inputRef.current;
                    if (input) {
                      input.setRangeText(
                        "",
                        Math.max(input.selectionStart! - 1, 0),
                        input.selectionEnd!,
                        "end",
                      );
                      const event = new Event("input", { bubbles: true });
                      input.dispatchEvent(event);
                    }
                  }
                  return;
                }
                applyToInput();
              }}
              disableButtonHold
              preventMouseDownDefault
              tabCharOnTab={false}
              mergeDisplay
              display={{
                "{space}": " ",
                "{numbers}": " ",
                "{specChars}": "*/?",
                "{ent}": " ",
                "{backspace}": "⌫",
                "{lock}": "caps lock ⇪",
                "{shift}": "⇧",
                "{abc}": "abc",
                "{tab}": " ",
                "{enter}": `${i18n.t("Tovább").toUpperCase()}⠀⠀(ENTER)⠀⠀⏎`,
              }}
              layout={{
                default: getLayout({
                  type:
                    (props.type === "taxnumber" ? "number" : props.type) ||
                    "text",
                  lang: language,
                  shift:
                    uppercase || (capitalize && !value) || mode === "shift",
                  mode,
                })!,
              }}
            />
          </div>,
          document.body.parentElement!,
        )}
    </>
  );
}

function getLayout(args: {
  type: InputType | (string & {});
  lang: Language;
  shift: boolean;
  mode: Mode;
}) {
  return getBaseLayout(args)
    .map((elem) =>
      getFeatureFlags().stickyKeyboard
        ? elem
        : elem
            .replace("{enter} ", "")
            .replace(" {enter}", "")
            .replace("{enter}", ""),
    )
    .filter((elem) => !!elem);
}

function getBaseLayout({
  type,
  lang,
  shift,
  mode,
}: {
  type: InputType | (string & {});
  lang: Language;
  shift: boolean;
  mode: Mode;
}) {
  if (type === "hidden" || type === "color") {
    return [];
  }
  if (mode === "specChars") {
    return [
      "_ . - : , + ! %",
      "/ = ( ) # & @ <",
      "{abc} > [ ] * $ {backspace}",
      "{enter}",
    ];
  }

  if (type === "username") {
    return [
      "0 1 2 3 4 5 6 7 8 9",
      "Q W E R T Y U I O P",
      "A S D F G H J K L",
      "Z X C V B N M {backspace}",
      "{enter}",
    ];
  }
  // Username or email on the login screen; the field is uppercased and the
  // backend also tries the lowercased email.
  if (type === "login") {
    return [
      "0 1 2 3 4 5 6 7 8 9",
      "Q W E R T Y U I O P -",
      "A S D F G H J K L + @",
      "Z X C V B N M _ . {backspace}",
      "{enter}",
    ];
  }
  if (type === "tel") {
    return ["1 2 3", "4 5 6", "7 8 9", "+ 0 {backspace}", "{enter}"];
  }
  if (type === "number") {
    return ["1 2 3", "4 5 6", "7 8 9", "0 {backspace}", "{enter}"];
  }

  if (type === "email") {
    return [
      "1 2 3 4 5 6 7 8 9 0",
      "q w e r t y u i o p -",
      "a s d f g h j k l + @",
      "z x c v b n m _ . {backspace}",
      "{enter}",
    ];
  }

  const bottomBar = "{shift} {space} {enter} {backspace}";

  if (type === "password") {
    return shift
      ? [
          "0 1 2 3 4 5 6 7 8 9",
          "Q W E R T Y U I O P",
          "A S D F G H J K L",
          "{specChars} Z X C V B N M",
          "{shift} {enter} {backspace}",
        ]
      : [
          "0 1 2 3 4 5 6 7 8 9",
          "q w e r t y u i o p",
          "a s d f g h j k l",
          "{specChars} z x c v b n m",
          bottomBar.replace("{space} ", ""),
        ];
  }

  return {
    hu: shift
      ? [
          "0 1 2 3 4 5 6 7 8 9",
          "É Á Ű Ő Ú Ö Ü Ó Í",
          "Q W E R T Y U I O P",
          "A S D F G H J K L",
          "Z X C V B N M - .",
          bottomBar,
        ]
      : [
          "0 1 2 3 4 5 6 7 8 9",
          "é á ű ő ú ö ü ó í",
          "q w e r t y u i o p",
          "a s d f g h j k l",
          "z x c v b n m - .",
          bottomBar,
        ],
    en: shift
      ? [
          "0 1 2 3 4 5 6 7 8 9",
          "Q W E R T Y U I O P",
          "A S D F G H J K L",
          "Z X C V B N M - .",
          bottomBar,
        ]
      : [
          "0 1 2 3 4 5 6 7 8 9",
          "q w e r t y u i o p",
          "a s d f g h j k l",
          "z x c v b n m - .",
          bottomBar,
        ],
    de: shift
      ? [
          "0 1 2 3 4 5 6 7 8 9",
          "Q W E R T Z U I O P Ü",
          "A S D F G H J K L Ö Ä",
          "Y X C V B N M ẞ - .",
          bottomBar,
        ]
      : [
          "0 1 2 3 4 5 6 7 8 9",
          "q w e r t z u i o p ü",
          "a s d f g h j k l ö ä",
          "y x c v b n m ß - .",
          bottomBar,
        ],
    sk: shift
      ? [
          "0 1 2 3 4 5 6 7 8 9",
          "Q W E R T Y U I O P",
          "A S D F G H J K L",
          "Z X C V B N M - .",
          bottomBar,
        ]
      : [
          "0 1 2 3 4 5 6 7 8 9",
          "q w e r t y u i o p",
          "a s d f g h j k l",
          "z x c v b n m - .",
          bottomBar,
        ],
  }[lang];
}
